/**
 * Project-context contract — Phase A freeze (decisions applied 2026-07-14).
 *
 * The header project selector (specs/changes/enterprise-project-organization,
 * project-selector-surfaces.md) persists the user's selected project across
 * the surface subdomains and deep links. These names and shapes are the
 * contract every consumer (React surfaces, the docs-portal island, e2e specs)
 * imports — change them only with a spec revision.
 *
 * Product decisions folded in:
 *  - path-addressable project views: `/p/{projectSlug}/…` on every surface,
 *    for bookmarking/sharing; the API resolves ids and slugs interchangeably;
 *  - "All projects" is always offered;
 *  - exactly one visible project auto-selects.
 */

/**
 * Cross-subdomain selection cookie (`Domain=.systemdox.com`, `SameSite=Lax`,
 * 30 days). Value: `{tenantSlug}:{projectId}` — an id only; the next session
 * resolves it against `GET /projects` and silently falls back to "All
 * projects" when it no longer resolves (never an error, never a name).
 */
export const PROJECT_COOKIE_NAME = 'sdx_project';

/**
 * Cross-subdomain SPACE selection cookie (workspace-organization Phase C,
 * scope-selector spec) — same `Domain=.systemdox.com`, `SameSite=Lax`, 30-day
 * contract as `sdx_project`. Value: `{tenantSlug}:{spaceId}`. A project
 * selection writes BOTH cookies (a project implies its space); clearing to
 * "All spaces" clears both. Resolves after the project cookie: path →
 * `?project=` → `?space=` → `sdx_project` (implies space) → `sdx_space` →
 * auto-select single project.
 */
export const SPACE_COOKIE_NAME = 'sdx_space';

/**
 * Deep-link query override for the space tier, below `?project=` in
 * precedence. Accepts a spaceId; unknown/archived/non-visible spaces
 * silently fall through to the next tier.
 */
export const SPACE_QUERY_PARAM = 'space';

/**
 * Path-addressable project views: `${PROJECT_PATH_PREFIX}/{projectSlug}/…`.
 * Highest precedence — a shared/bookmarked path always wins:
 * path → `?project=` → cookie → none (= All projects). Path and query
 * resolution rewrite the cookie. A slug change releases the old slug
 * (bookmarks to it break; an alias registry is reserved, unimplemented).
 */
export const PROJECT_PATH_PREFIX = '/p';

/**
 * Deep-link query override, second in precedence after the path. Accepts a
 * projectId or slug.
 */
export const PROJECT_QUERY_PARAM = 'project';

/**
 * Which plane `GET /projects` lists in. Named for what it SELECTS, not for who
 * is asking — an earlier draft spelled this `?visible=me`, which had exactly one
 * legal value and so carried no information.
 *
 * `read` (the default) — projects in spaces the caller holds a read scope on.
 *   The header selector MUST use this: selecting a project filters every surface
 *   to its repos, so offering one whose repos the caller cannot read would filter
 *   them all to nothing.
 *
 * `manage` — the above, plus the admin-plane discovery rule: an admin also sees
 *   non-sealed spaces they hold no content grant on, so /settings/github can
 *   manage projects there. Strictly wider. Ask for it explicitly.
 *
 * Sealed spaces need an explicit grant in both planes. A non-admin asking for
 * `manage` just gets their read footing — the plane widens for admins only, so
 * it is never an escalation.
 */
export type ProjectScope = 'read' | 'manage';

/** One entry in the project listing (`GET /projects?scope=&q=`). */
export interface SelectorProject {
  projectId: string;
  /** Tenant-unique URL slug — the path-addressable identity. */
  slug: string;
  name: string;
  spaceId: string;
  repoCount: number;
  updatedAt: string;
}

/** The selector response. `total` counts everything visible pre-page. */
export interface SelectorResponse {
  projects: SelectorProject[];
  total: number;
}

/**
 * One entry in the scope selector's space tier (workspace-organization
 * Phase C). Derived from `GET /spaces` minus archived spaces; the tier is
 * hidden entirely when the caller can see ≤ 1 space, so single-space tenants
 * keep exactly the project-only selector.
 */
export interface SelectorSpace {
  spaceId: string;
  name: string;
  isDefault?: boolean;
}
